import { useState } from 'react';
import { parseISO } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DateCalendar } from '@/components/common/DateCalendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CALENDAR_POPOVER_CLASS } from '@/styles/floatingPanel';
import { formatDate, formatDisplayDate, isOutsideDateLimits } from '@/utils/date';
import type { FilterField } from './advancedFilter.types';
import { FilterPopoverTrigger } from './FilterPopoverTrigger';

interface FilterDateFieldProps {
  field: FilterField;
  value: string;
  onChange: (value: string) => void;
  minDate: string | undefined;
  maxDate: string | undefined;
}

export function FilterDateField({
  field,
  value,
  onChange,
  minDate,
  maxDate,
}: FilterDateFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedDate = value ? parseISO(value) : undefined;
  const placeholder = field.placeholder ?? 'Pick a date';

  function handleSelect(date: Date | undefined) {
    onChange(date ? formatDate(date) : '');
    setIsOpen(false);
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <FilterPopoverTrigger
            icon={CalendarIcon}
            text={selectedDate ? formatDisplayDate(selectedDate) : placeholder}
            hasValue={selectedDate !== undefined}
            clearLabel={`Clear ${field.label}`}
            onClear={() => onChange('')}
          />
        }
      />
      <PopoverContent className={CALENDAR_POPOVER_CLASS} align="start">
        <DateCalendar
          mode="single"
          selected={selectedDate}
          {...(selectedDate ? { defaultMonth: selectedDate } : {})}
          onSelect={handleSelect}
          disabled={(date) => isOutsideDateLimits(date, minDate, maxDate)}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
